import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getIndiceDetails } from '../Data/dataApi';
import { IndiceDTO } from '../Models/IndiceDTO';
import { Button } from '../Components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAuth } from "../Auth/autoContext";
import { Helmet } from 'react-helmet-async';

const Details = () => {
  const { symbol } = useParams(); // symbol
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();
  const [isMobile, setIsMobile] = useState(false);

  const [indice, setIndice] = useState<IndiceDTO | null>(null);

  const returnUrl = new URLSearchParams(location.search).get('returnUrl') || 'list';

  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 1140);
    };

    check();
    window.addEventListener("resize", check);

    return () => window.removeEventListener("resize", check);
  }, []);

  // const getRecommendationClass = (rec: string): string => {
  //   switch (rec) {
  //     case 'Strong Buy':
  //       return 'text-strong-buy';
  //     case 'Buy':
  //       return 'text-buy';
  //     case 'Hold':
  //       return 'text-hold';
  //     case 'Sell':
  //       return 'text-sell';
  //     case 'Strong Sell':
  //       return 'text-strong-sell';
  //     default:
  //       return '';
  //   }
  // };

  //  Configuration for using translation with json file
  const { t } = useTranslation();

  useEffect(() => {
    if (symbol) {
      getIndiceDetails(symbol).then(setIndice);
    }
  }, [symbol]);

  if (!indice) return <div>{t('details.loading')}</div>;

  const trainingData = indice.trainingData ?? [];

  const getRecommendationClass = (rec: string): string => {
    switch (rec) {
      case 'Strong Buy': return 'text-strong-buy';
      case 'Buy': return 'text-buy';
      case 'Hold': return 'text-hold';
      case 'Sell': return 'text-sell';
      case 'Strong Sell': return 'text-strong-sell';
      default: return 'text-hold';
    }
  };

  const renderMobileCards = () => (
    <div className="card-container">
      {trainingData
        .slice()
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 20)
        .map((item, index) => (
          <div key={index} className="indice-card">

            <div className="card-header" id='text-mobile'>
              <span>{t('details.table.date')}</span>
              <span>{new Date(item.date).toLocaleDateString('fr', { timeZone: 'UTC' })}</span>
            </div>

            <div className="card-row" id='text-mobile'>
              <span>{t('details.table.price')}</span>
              <span>{item.currentPrice.toFixed(3)}</span>
            </div>

            <div className="card-row">
              <span>{t('details.table.change')}</span>
              <span className={item.change > 0 ? "color-positive" : "color-negative"}>
                {item.change.toFixed(3)}
              </span>
            </div>

            <div className="card-row">
              <span>{t('details.table.open')}</span>
              <span>
                {item.open.toFixed(3)}
              </span>
            </div>

            <div className="card-row">
              <span>{t('details.table.%change')}</span>
              <span className={item.changePercent > 0 ? "color-positive" : "color-negative"}>
                {item.changePercent.toFixed(3)}%
              </span>
            </div>

            <div className="card-row">
              <span>{t('details.table.prevClose')}</span>
              <span>{item.prevPrice.toFixed(3)}</span>
            </div>

            <div className="card-row">
              <span>High / Low</span>
              <span>{item.high.toFixed(3)} / {item.low.toFixed(3)}</span>
            </div>

            <div className="card-row">
              <span>{t('details.table.volume')}</span>
              <span>{item.volume.toFixed(0)}</span>
            </div>

            {/* <div className="card-row">
              <span>{t('details.table.type')}</span>
              <span>{indice.quoteType}</span>
            </div>

            <div className="card-row">
              <span>{t('details.table.stockmarket')}</span>
              <span>{indice.bourse}</span>
            </div>

            <div className="card-row">
              <span>{t('details.table.exchange')}</span>
              <span>{indice.exchange}</span>
            </div> */}
            {(role === 'premium' || role === 'public') && (
              <>
                <div className="card-row">
                  <span>{t('details.table.tendency')}</span>
                  <span className={item.label ? "color-positive" : "color-negative"}>
                    {item.label ? "UP" : "DOWN"}
                  </span>
                </div>
              </>
            )}
            {role === "premium" && (
              <>
                <div className="card-row">
                  <span>{t('details.table.recommendation')}</span>
                  <span className={getRecommendationClass(item.raccomandation)}>
                    {item.raccomandation ?? "Hold"}
                  </span>
                </div>

                <div className="card-row">
                  <span>{t('details.table.probability')}</span>
                  <span className={item.probability > 0.5 ? "color-positive" : "color-negative"}>
                    {(item.probability * 100).toFixed(1)}%
                  </span>
                </div>

                <div className="card-row">
                  <span>{t('details.table.predictedDirection')}</span>
                  <span className={item.predictedIsIncreasing === null ? "" : item.predictedIsIncreasing ? "color-positive" : "color-negative"}>
                    {item.predictedIsIncreasing === null ? "N/A" : item.predictedIsIncreasing ? t('list.table.yes') : t('list.table.no')}
                  </span>
                </div>

                <div className="card-row">
                  <span>{t('details.table.strongMoveProbability')}</span>
                  <span className={item.strongMoveProbability === null || !Number.isFinite(item.strongMoveProbability) ? "" : item.strongMoveProbability >= 0.50 ? "color-positive" : "color-negative"}>
                    {item.strongMoveProbability === null || !Number.isFinite(item.strongMoveProbability) ? "N/A" : `${(item.strongMoveProbability * 100).toFixed(1)}%`}
                  </span>
                </div>
              </>
            )}

          </div>
        ))}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>
          {indice.name} ({indice.symbol}) Stock Analysis & Prediction | Stockifly
        </title>

        <meta
          name="description"
          content={`Live ${indice.name} (${indice.symbol}) stock price, AI prediction, RSI, MACD, moving averages, technical analysis and financial ratios on Stockifly.`}
        />

        <link
          rel="canonical"
          href={`https://stockifly.app/stock/${encodeURIComponent(indice.symbol)}`}
        />

        <meta
          property="og:title"
          content={`${indice.name} (${indice.symbol}) Stock Analysis | Stockifly`}
        />

        <meta
          property="og:description"
          content={`Stock analysis, historical prices and AI-powered predictions for ${indice.name} (${indice.symbol}).`}
        />

        <meta
          property="og:url"
          content={`https://stockifly.app/stock/${indice.symbol}`}
        />

        <meta property="og:type" content="website" />
      </Helmet>

      <div className='list-container'>
        <h1 className="text-2xl font-bold mb-4">
          {indice.name} ({indice.symbol}) Stock Analysis & Prediction
        </h1>

        <p className="mb-4">
          View the latest {indice.name} ({indice.symbol}) stock price,
          historical performance, market trend and AI-powered stock analysis
          on Stockifly. Explore price movements, volume and technical data
          to better understand {indice.symbol} market performance.
        </p>

        <div className="mb-4">
          <Button onClick={() => navigate(`/${returnUrl}`)}>
            {t('details.back')} {returnUrl}
          </Button>
        </div>

        <h2 className="text-xl font-semibold mb-2">{t('details.history')}</h2>
        {isMobile ? (
          renderMobileCards()
        ) : (
          <table className="table">
            <thead className="bg-gray-200">
              <tr>
                <th>{t('details.table.price')}</th>
                {/* <th>{t('details.table.change')}</th> */}
                <th>{t('details.table.open')}</th>
                <th>{t('details.table.%change')}</th>
                <th>{t('details.table.prevClose')}</th>
                <th>{t('details.table.high')}</th>
                <th>{t('details.table.low')}</th>
                <th>{t('details.table.volume')}</th>
                {/* <th>{t('details.table.type')}</th>
                <th>{t('details.table.stockmarket')}</th>
                <th>{t('details.table.exchange')}</th> */}
                {/* {(role === 'premium' || role === 'public') && (
                  <>
                    <th>Tendance</th>
                  </>
                )} */}
                {role === 'premium' && (
                  <>
                    <th>{t('details.table.recommendation')}</th>
                    <th>{t('details.table.probability')}</th>
                    <th>{t('details.table.predictedDirection')}</th>
                    <th>{t('details.table.strongMoveProbability')}</th>
                  </>
                )}
                <th>{t('details.table.date')}</th>
              </tr>
            </thead>
            <tbody>
              {trainingData
                .slice()
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // tri décroissant
                .slice(0, 20) // prends les 20 plus récents
                .map((item, index) => (
                  <tr key={index}>
                    <td>{item.currentPrice.toFixed(3)}</td>
                    {/* <td className={item.change > 0 ? "color-positive" : "color-negative"}>{item.change.toFixed(3)}</td> */}
                    <td>{item.open.toFixed(3)}</td>
                    <td className={item.changePercent > 0 ? "color-positive" : "color-negative"}>
                      ({item.changePercent.toFixed(3)}%)
                    </td>
                    <td>{item.prevPrice.toFixed(3)}</td>
                    <td>{item.high.toFixed(3)}</td>
                    <td>{item.low.toFixed(3)}</td>
                    <td>{item.volume}</td>
                    {/* <td>{indice.quoteType}</td> */}
                    {/* <td>{indice.bourse}</td> */}
                    {/* <td>{indice.exchange}</td> */}
                    {/* {(role === 'premium' || role === 'public') && (
                      <td className={item.label ? "color-positive" : "color-negative"}>
                        {item.label ? "UP" : "DOWN"}
                      </td>
                    )} */}
                    {role === 'premium' && (
                      <>
                        <td className={`rec-cell ${getRecommendationClass(item.raccomandation)}`}>{item.raccomandation ?? 'Hold'}</td>
                        <td className={item.probability > 0.49 ? "color-positive" : "color-negative"}>{(item.probability * 100).toFixed(1)}%</td>
                        <td className={item.predictedIsIncreasing === null ? "" : item.predictedIsIncreasing ? "color-positive" : "color-negative"}>
                          {item.predictedIsIncreasing === null ? "N/A" : item.predictedIsIncreasing ? t('list.table.yes') : t('list.table.no')}</td>
                        <td className={item.strongMoveProbability === null || !Number.isFinite(item.strongMoveProbability) ? "" : item.strongMoveProbability >= 0.50 ? "color-positive" : "color-negative"}>
                          {item.strongMoveProbability === null || !Number.isFinite(item.strongMoveProbability) ? "N/A" : `${(item.strongMoveProbability * 100).toFixed(1)}%`}</td>
                      </>
                    )}
                    <td>
                      {new Date(item.date).toLocaleDateString('fr', { timeZone: 'UTC' })}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default Details;
